import {
  InputFieldsType,
  designTypeFieldsRegisterPage,
  orderedRegisterPageFields,
} from '@composites/InboundButton/utils';
import { getLanguage } from '@store/slices/configSlice';
import { printPdf, useMobileWidth } from '@utils/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '@components/Button';
import Spinner from '@components/Spinner';
import { DefaultBooleanValues } from '@composites/InboundButton';
import { getEventDetails } from '@store/slices/eventSlice';
import {
  ACCOUNT_ID,
  ROOT_URL,
  useGetPublicEventAdvertisement,
  usePublicAllAgendas,
  usePublicAttendees,
  usePublicCoLocatedEventsByEventId,
  usePublicExhibitions,
  usePublicPortfolioByAccount,
} from '@utils/apiHooks';

import { groupAndSortByDate, safeJSONParse, uniqBy, getUrl } from '@utils/fn';
import { useInbound } from '@utils/Inbound/useInbound';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  PublicExhibitionItem,
  PublicSessionItem,
} from '@hubs101/booking-api-skd-client/lib/types';
import BusinessPartnerPdfContent from '@composites/PDF/BusinessPartnerPdfContent';
import { getUniquePdfDays } from '@utils/time';
import { agendaPdfDays } from '@pages/Agenda/helper';
import { Ticket } from '@composites/Tickets';

interface DownloadPartnerProspectusProps {
  label: string;
  buttonType: string;
  eventId?: string;
  inboundValues?: {
    nonBooleanValues: Record<string, string>;
    booleanValues: DefaultBooleanValues;
  };
}

const DownloadPartnerProspectus = ({
  label,
  buttonType,
  inboundValues,
  eventId,
}: DownloadPartnerProspectusProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMobileWidth();
  const language = useSelector(getLanguage);
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [showModal, setShowModal] = useState<boolean>(false);
  const [checkedApproval, setCheckedApproval] = useState<boolean>(false);
  const { inboundValue, actionInbound, registerInbound, shouldShowForm } =
    useInbound();

  const [eventSeries, setEventSeries] = useState([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [allTickets, setAllTickets] = useState<Ticket[]>([]);

  const { data: attendees } = usePublicAttendees({
    eventId: sluggedEvent?.id,
    enabled: sluggedEvent?.id?.length > 0,
    colocated: sluggedEvent?.colocated,
  });

  const { data: portfolios } = usePublicPortfolioByAccount({
    accountId: ACCOUNT_ID,
  });

  const { data: advertisements = [] } = useGetPublicEventAdvertisement({
    eventId: sluggedEvent?.id || '',
  });

  const {
    isSuccess,
    data: coLocatedEvents,
    isLoading: isCoLocatedEventsLoading,
  } = usePublicCoLocatedEventsByEventId({
    eventId: sluggedEvent?.id ?? '',
    enabled: (sluggedEvent?.id?.length ?? 0) > 0 && !!sluggedEvent?.colocated,
  });

  const selectedEventsIds = useMemo(() => {
    if (sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated)
      return coLocatedEvents?.map(e => e.id) ?? ([] as string[]);
    return [sluggedEvent?.id];
  }, [coLocatedEvents, sluggedEvent?.colocated, sluggedEvent?.id]);

  const agendas: any =
    usePublicAllAgendas({
      page: 0,
      language: sluggedEvent?.language,
      eventId: sluggedEvent?.id,
      enabled: sluggedEvent?.id?.length > 0,
      colocated: sluggedEvent?.colocated,
    }) || [];
  const agendasSessions = useMemo(() => {
    const raw = agendas?.data;

    const sessions: any[] = Array.isArray(raw)
      ? raw.map((e: any) => e?.sessions).flat()
      : (raw?.sessions ?? []);

    return sessions
      .filter(e => Boolean(e))
      .map(e => ({
        ...e,
      }));
  }, [agendas?.data]);

  const agendaPdfDates = useMemo(() => {
    const agendaDaysPdfFormat = getUniquePdfDays(
      agendasSessions?.map(e => e?.start_date) ?? []
    );
    const result = agendaDaysPdfFormat?.map((e, index) => {
      return agendaDaysPdfFormat?.length === 1
        ? agendaDaysPdfFormat[index]
        : `${t(agendaPdfDays[index])} - ${agendaDaysPdfFormat[index]}`;
    });
    return result;
  }, [agendasSessions, t]);

  const groupedAgendasSessions = useMemo(
    () => Object.values(groupAndSortByDate(agendasSessions, 'start_date')),
    [agendasSessions]
  );

  const agendasSpeakers = useMemo(
    () =>
      uniqBy(
        agendasSessions
          ?.map(e => e?.speakers)
          ?.flat()
          ?.filter(notNull => notNull),
        'profile'
      ),
    [agendasSessions]
  );

  const exhibitions = usePublicExhibitions({
    page: 0,
    language: sluggedEvent?.language,
    eventIds: selectedEventsIds,
    enabled:
      selectedEventsIds?.length > 0 ||
      (sluggedEvent?.colocated && isSuccess && selectedEventsIds?.length > 0),
  });

  const allExhibitions = useMemo(
    () =>
      exhibitions
        ?.map(a => a?.data)
        ?.flat()
        ?.filter(notNull => notNull) as PublicExhibitionItem[],
    [exhibitions]
  );

  useEffect(() => {
    if (sluggedEvent?.id) {
      fetch(`${ROOT_URL}/tickets?event=${sluggedEvent?.id}`)
        .then(async response => await response.json())
        .then(data => data.result.data)
        .then(ticketsData => {
          const relevantTickets = ticketsData
            .filter(
              (ticket: Ticket) =>
                ticket.visibility === 'public' && ticket.attendee_type === 'de'
            )
            .sort((a: Ticket, b: Ticket) => a.priority - b.priority);

          setTickets(relevantTickets);
          setAllTickets(ticketsData);
        })
        .catch(err => {
          console.error('error = ', err);
        });

      fetch(
        `${ROOT_URL}/accounts/${sluggedEvent?.account}/portfolios/${sluggedEvent?.portfolio?.slug}/events?limit=100&start_date=>${new Date().toISOString().split('T')[0]}`
      )
        .then(async response => await response.json())
        .then(data => data.result.data)
        .then((data: any) => {
          setEventSeries(data);
        })
        .catch(err => {
          console.error('error = ', err);
        });
    }
  }, [sluggedEvent]);

  const generatePartnerProspectusPDF = useCallback(() => {
    const doc = (
      <BusinessPartnerPdfContent
        event={sluggedEvent}
        coLocatedEvents={coLocatedEvents}
        portfolios={portfolios}
        theme={theme}
        groupedAgendasSessions={groupedAgendasSessions}
        agendaPdfDates={agendaPdfDates}
        agendaSpeakers={agendasSpeakers}
        exhibitions={allExhibitions}
        advertisements={advertisements}
        attendees={attendees?.attendees}
        tickets={tickets}
        allTickets={allTickets}
        eventSeries={eventSeries}
      />
    );
    setLoading(true);
    printPdf(doc, `${t('business-partner')}-${sluggedEvent?.name}`)
      .catch(console.error)
      .finally(() => {
        setLoading(false);
      });
  }, [
    sluggedEvent,
    coLocatedEvents,
    portfolios,
    groupedAgendasSessions,
    agendasSpeakers,
    allExhibitions,
    advertisements,
    attendees,
    tickets,
    allTickets,
    eventSeries,
    language,
    t,
    theme,
  ]);

  const inputElements = useMemo(
    () =>
      orderedRegisterPageFields
        .map(item => {
          if (inboundValues?.booleanValues?.[item]?.visible) {
            const designTypeParameters = designTypeFieldsRegisterPage?.find(
              e => e.id === item
            );
            return {
              ...designTypeParameters,
              ...inboundValues?.booleanValues?.[item],
            };
          }
          return undefined;
        })
        ?.filter(item => item) as InputFieldsType,
    [inboundValues?.booleanValues]
  );

  const defaultValues: Record<string, string> = useMemo(() => {
    return inputElements.reduce<Record<string, string>>((acc, key) => {
      acc[key.id] = safeJSONParse(inboundValue)?.[key.id] ?? '';
      return acc;
    }, {});
  }, [inboundValue, inputElements]);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Clear error for a field when user starts typing in it
  const handleChange = (fieldName: string) => (value: string) => {
    clearErrors(fieldName);
  };

  const onSubmit = useCallback(
    async (data: Record<string, string>) => {
      if (!checkedApproval) return;

      await registerInbound(
        {
          ...data,
          event: sluggedEvent?.id,
          path: window.location.href,
          action: 'download_bp_packages',
        },
        () => {
          generatePartnerProspectusPDF();
          setShowModal(false);
        }
      );
    },
    [checkedApproval, inboundValue, registerInbound, sluggedEvent?.id]
  );

  return (
    <>
      <Button
        icon={loading ? <Spinner size="2rem" /> : undefined}
        title={label}
        backgroundColor={sluggedEvent?.design?.themeColor}
        hoverBackgroundColor={theme.color.mobilityGrey}
        textColor={theme.color.white}
        lineHeight={theme.lineHeight.height125}
        fontSize={theme.fontSize.size20}
        borderRadius={theme.borderRadius.radius10}
        padding={isMobile ? '1.1rem 2.22rem' : '1.1rem 3rem'}
        width={isMobile ? '100%' : 'max-content'}
        disabled={loading}
        onClick={() => {
          void (async () => {
            if (shouldShowForm) {
              // Redirect
              const path = getUrl(t('bp-packages-url'));
              const href = `${window.location.origin}/events/${sluggedEvent?.slug}/${path}`;
              window.location.href = href;
              return;
            }

            // Has inbound value => download + inbound
            generatePartnerProspectusPDF();
            await actionInbound({
              ...JSON.parse(inboundValue ?? '{}'),
              event: sluggedEvent?.id,
              path: window.location.href,
              action: 'download_bp_packages',
            });
          })();
        }}
      />
    </>
  );
};

export default DownloadPartnerProspectus;
